import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { CardNetPurchaseService } from '../../../../services/cardnetPurchaseService';

/* ===== TYPE DECLARATIONS ===== */
declare global {
  interface Window {
    PWCheckout?: {
      Bind: (eventName: string, callbackName: string) => void;
      SetProperties: (props: Record<string, any>) => void;
      AddActionButton: (...buttonIds: string[]) => void;
    };
    OnYapTokenReceived?: (token: any) => void;
  }
}

/* ===== CONSTANTS ===== */
const CARDNET_PUBLIC_KEY = 'mfH9CqiAFjFQh_gQR_1TQG_I56ONV7HQ';

// Session storage keys for reload management
const TRANSITION_FLAG = 'cn_transition_in_progress';
const VISIT_ID_KEY = 'cardnet_visit_id';
const RELOADED_KEY = 'cardnet_reloaded';

/* ===== HELPER FUNCTIONS ===== */
const dlog = (...args: any[]) => {
  const t = new Date().toISOString().split('T')[1].replace('Z', '');
  console.log(`[YAP:CN ${t}]`, ...args);
};

const hasPWCheckout = (): boolean => {
  return !!window.PWCheckout;
};

/* ===== MAIN COMPONENT ===== */
const CardNetCheckoutScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const { mesaId, subtotal, tipPercentage, tipAmount, waiterName, staffId } = (location.state || {}) as {
    mesaId?: string;
    subtotal?: number;
    tipPercentage?: number;
    tipAmount?: number;
    waiterName?: string;
    staffId?: string;
  };

  const [checkoutReady, setCheckoutReady] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [coverVisible, setCoverVisible] = useState<boolean>(() => 
    sessionStorage.getItem(TRANSITION_FLAG) === '1'
  );
  const [coverOpaque, setCoverOpaque] = useState<boolean>(true);
  
  const initOnceRef = useRef(false);
  const total = (subtotal || 0) + (tipAmount || 0);

  /* ===== ONE-TIME RELOAD WITH SMOOTH COVER ===== */
  useEffect(() => {
    const storedVisitId = sessionStorage.getItem(VISIT_ID_KEY);
    const hasReloaded = sessionStorage.getItem(RELOADED_KEY);

    // First visit - set up for reload
    if (!storedVisitId) {
      sessionStorage.setItem(VISIT_ID_KEY, `${Date.now()}_${Math.random()}`);
      sessionStorage.setItem(RELOADED_KEY, '0');
      sessionStorage.setItem(TRANSITION_FLAG, '1');
      setCoverVisible(true);
      setCoverOpaque(true);
      
      // Reload after short delay
      setTimeout(() => {
        window.location.replace(window.location.href);
      }, 120);
      return;
    }

    // After reload - show cover briefly
    if (storedVisitId && hasReloaded === '0') {
      sessionStorage.setItem(RELOADED_KEY, '1');
      sessionStorage.setItem(TRANSITION_FLAG, '1');
      setCoverVisible(true);
      setCoverOpaque(true);
    }
  }, []);

  /* ===== FADE OUT COVER WHEN READY ===== */
  useEffect(() => {
    if (!checkoutReady) return;
    
    // Start fade out
    const fadeTimer = setTimeout(() => setCoverOpaque(false), 150);
    
    // Remove cover completely
    const removeTimer = setTimeout(() => {
      setCoverVisible(false);
      sessionStorage.removeItem(TRANSITION_FLAG);
      sessionStorage.removeItem(VISIT_ID_KEY);
      sessionStorage.removeItem(RELOADED_KEY);
    }, 450);
    
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(removeTimer);
    };
  }, [checkoutReady]);

  /* ===== CARDNET INITIALIZATION ===== */
  useEffect(() => {
    if (initOnceRef.current) return;
    
    dlog('🔄 Waiting for PWCheckout to load...');
    
    let attempts = 0;
    const maxAttempts = 50;
    
    const waitForPWCheckout = () => {
      if (hasPWCheckout()) {
        initOnceRef.current = true;
        dlog('✅ PWCheckout loaded successfully');
        initializeCardNet();
        setCheckoutReady(true);
        return;
      }
      
      if (attempts++ < maxAttempts) {
        setTimeout(waitForPWCheckout, 100);
      } else {
        dlog('❌ PWCheckout failed to load after 5 seconds');
        alert('Error: No se pudo cargar el sistema de pago. Por favor recarga la página.');
      }
    };
    
    waitForPWCheckout();
  }, [total, mesaId]);

  /**
   * Initialize CardNet following official documentation EXACTLY
   */
  const initializeCardNet = () => {
    try {
      if (!hasPWCheckout()) {
        dlog('❌ PWCheckout not available');
        return;
      }

      const PW = window.PWCheckout!;
      
      dlog('🔧 Initializing CardNet (exact docs pattern)');
      dlog('📊 Amount:', total.toFixed(2), 'DOP');

      // STEP 1: Configure CardNet properties (exactly from docs)
      PW.SetProperties({
        name: 'YAP - Pago en Mesa',
        description: mesaId ? `Mesa ${mesaId}` : 'Pago en restaurante',
        currency: 'DOP',
        amount: total.toFixed(2),
        lang: 'ESP',
        form_id: 'yap_payment_form',        // ← Links to our form
        checkout_card: 1,                    // ← Go directly to card entry
        autoSubmit: 'false',                 // ← From docs: let us handle submit
        empty: 'true',                       // ← Hide CardNet branding
        button_label: 'Pagar',
      });
      
      dlog('✅ Properties configured (autoSubmit: false)');

      // STEP 2: Associate payment button (CardNet intercepts clicks)
      PW.AddActionButton('cardnet_pay_btn');
      dlog('✅ Button associated - CardNet will handle clicks');

      // STEP 3: Monitor for token insertion (since autoSubmit is false)
      dlog('👀 Setting up token detection...');
      
      const tokenInput = document.getElementById('PWToken') as HTMLInputElement;
      
      if (!tokenInput) {
        throw new Error('PWToken input not found in DOM');
      }

      // Use interval polling (most reliable for input value changes)
      const checkInterval = setInterval(() => {
        const tokenValue = tokenInput.value;
        
        // Check if token has been inserted by CardNet
        if (tokenValue && tokenValue.length > 20 && !processing) {
          dlog('🎉 TOKEN INSERTED BY CARDNET!');
          dlog('📦 Token:', tokenValue.slice(0, 40) + '...');
          dlog('📏 Length:', tokenValue.length);
          
          // Stop checking
          clearInterval(checkInterval);
          
          // Small delay to ensure CardNet finished its work
          setTimeout(() => {
            dlog('✅ Submitting form with token...');
            const form = document.getElementById('yap_payment_form') as HTMLFormElement;
            if (form) {
              // Trigger the form's onSubmit handler
              form.dispatchEvent(new Event('submit', { 
                bubbles: true, 
                cancelable: true 
              }));
            }
          }, 300);
        }
      }, 200); // Check every 200ms

      // Safety: Stop checking after 2 minutes
      setTimeout(() => {
        clearInterval(checkInterval);
        dlog('⏱️ Token check timeout (2 minutes)');
      }, 120000);

      dlog('✅ Token detector active (polling every 200ms)');
      dlog('🎯 CardNet ready! Click button to start payment.');

    } catch (error) {
      dlog('❌ FATAL ERROR initializing CardNet:', error);
      console.error('Full error:', error);
      alert('Error crítico al configurar CardNet. Por favor recarga la página.');
    }
  };

  /**
   * Process payment with token from CardNet
   */
  const processPaymentWithToken = async (tokenId: string) => {
    dlog('💳 Processing payment with token...');
    dlog('Token (first 30 chars):', tokenId.slice(0, 30) + '...');
    
    setProcessing(true);

    try {
      const result = await CardNetPurchaseService.processPurchase({
        mesaId: String(mesaId || ''),
        cardNetToken: tokenId,
        subtotal: Number(subtotal || 0),
        tipAmount: Number(tipAmount || 0),
        tipPercentage: Number(tipPercentage || 0),
      });

      dlog('📥 Backend response:', result);

      if (result.approved) {
        dlog('🎉 PAYMENT APPROVED!');
        dlog('Transaction ID:', result.transactionId);
        dlog('Authorization Code:', result.authorizationCode);
        
        navigate('/payment-success', {
          state: {
            transactionId: result.transactionId,
            authorizationCode: result.authorizationCode,
            amount: total,
            mesaId: String(mesaId || ''),
            serverName: waiterName || null,
            staffId: staffId || null,
          },
          replace: true,
        });
      } else {
        dlog('❌ PAYMENT DECLINED');
        dlog('Reason:', result.message);
        setProcessing(false);
        
        alert(`Pago declinado\n\n${result.message || 'Por favor intenta de nuevo.'}`);
      }
    } catch (error: any) {
      dlog('❌ Error processing payment:', error);
      console.error('Full error:', error);
      setProcessing(false);
      
      alert('Error al procesar el pago. Por favor intenta de nuevo.');
    }
  };

  /* ===== RENDER ===== */
  return (
    <div className="min-h-screen bg-white w-full overflow-x-hidden">
      <style>{`
        #PWModal, 
        #custom_modal,
        #custom_iframe,
        [id*="PW"],
        iframe[src*="cardnet"],
        iframe[src*="Capture"] {
          z-index: 2147483647 !important;
          position: fixed !important;
        }

        /* Loading cover styles */
        .cn-cover {
          position: fixed;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(180deg, rgba(255,255,255,.98), rgba(249,250,251,.98));
          backdrop-filter: blur(2px);
          z-index: 2147483648;
          opacity: 1;
          transition: opacity 300ms ease;
        }
        
        .cn-cover--hidden {
          opacity: 0;
          pointer-events: none;
        }
        
        .cn-spinner {
          width: 36px;
          height: 36px;
          border-radius: 9999px;
          border: 3px solid #e5e7eb;
          border-top-color: #111827;
          animation: spin 1s linear infinite;
        }
        
        .cn-badge {
          margin-top: 12px;
          color: #4b5563;
          font-size: 12px;
          letter-spacing: .02em;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center z-10">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100"
          aria-label="Volver"
        >
          <ChevronLeft className="w-6 h-6 text-gray-600" />
        </button>
        <h1 className="flex-1 text-center text-lg font-semibold text-gray-900 pr-10">
          Método de pago
        </h1>
      </div>

      <div className="max-w-md mx-auto px-4 sm:px-6 py-6 pb-24">
        {!checkoutReady && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">⏳ Cargando sistema de pago...</p>
          </div>
        )}

        {/* Payment Summary */}
        <div className="bg-gray-50 rounded-lg p-6 mb-6">
          <h3 className="font-semibold text-lg mb-4">Resumen</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Subtotal</span>
              <span className="font-medium">RD$ {(subtotal || 0).toFixed(2)}</span>
            </div>
            {Number(tipAmount) > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Propina ({tipPercentage || 0}%)</span>
                <span className="font-medium">RD$ {Number(tipAmount).toFixed(2)}</span>
              </div>
            )}
            <div className="border-t pt-2 mt-2 flex justify-between text-lg font-bold">
              <span>Total</span>
              <span>RD$ {total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Security notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-800 font-medium mb-2">🔒 Pago seguro con CardNet</p>
          <p className="text-xs text-blue-700">
            Tu información de tarjeta se procesará de forma segura a través de CardNet. 
            No almacenamos datos de tu tarjeta.
          </p>
        </div>

        {/* 
          CRITICAL: CardNet form
          This form MUST exist with these exact IDs
        */}
        <form
          id="yap_payment_form"
          method="post"
          action=""
          style={{
            position: 'absolute',
            left: '-9999px',
            top: '0',
            width: '1px',
            height: '1px',
            opacity: 0,
            overflow: 'hidden',
          }}
          onSubmit={(e) => {
            e.preventDefault();
            dlog('📝 Form auto-submit triggered by CardNet');
            
            const tokenInput = document.getElementById('PWToken') as HTMLInputElement;
            const token = tokenInput?.value;
            
            if (token && token.length > 10) {
              dlog('✅ Token found in PWToken field:', token.slice(0, 30) + '...');
              dlog('🚀 Processing payment with token...');
              processPaymentWithToken(token);
            } else {
              dlog('❌ Form submitted but no valid token found');
              dlog('Token value:', token);
              alert('Error: No se pudo obtener el token de pago.\nPor favor intenta de nuevo.');
            }
          }}
        >
          <input 
            type="hidden" 
            id="PWToken" 
            name="PWToken" 
            defaultValue=""
          />
          <input type="hidden" name="amount" value={total.toFixed(2)} />
          <input type="hidden" name="currency" value="DOP" />
        </form>

        {/* Payment instructions */}
        <div className="space-y-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
              1
            </div>
            <div>
              <p className="font-medium">Haz clic en "Pagar con tarjeta"</p>
              <p className="text-sm text-gray-600">Se abrirá el formulario seguro de CardNet</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
              2
            </div>
            <div>
              <p className="font-medium">Ingresa los datos de tu tarjeta</p>
              <p className="text-sm text-gray-600">Número, fecha de vencimiento y CVV</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
              3
            </div>
            <div>
              <p className="font-medium">Confirma el pago</p>
              <p className="text-sm text-gray-600">El pago se procesará automáticamente</p>
            </div>
          </div>
        </div>
      </div>

      {/* 
        Payment Button
        CRITICAL: id="cardnet_pay_btn" must match AddActionButton call
        NO onClick handler - CardNet intercepts the click
      */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-10">
        <div className="max-w-md mx-auto px-4 sm:px-6 py-4">
          <button
            id="cardnet_pay_btn"
            type="button"
            disabled={!checkoutReady || processing}
            className="w-full bg-black text-white py-4 rounded-full text-lg font-bold hover:bg-gray-800 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {!checkoutReady ? 'Cargando...' : processing ? 'Procesando...' : 'Pagar con tarjeta'}
          </button>
        </div>
      </div>

      {/* One-time reload cover */}
      {coverVisible && (
        <div className={`cn-cover ${coverOpaque ? '' : 'cn-cover--hidden'}`}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div className="cn-spinner" />
            <div className="cn-badge">Preparando pago seguro…</div>
          </div>
        </div>
      )}

      {/* Processing overlay (after payment) */}
      {processing && (
        <div 
          className="fixed inset-0 bg-white flex items-center justify-center" 
          style={{ zIndex: 2147483647 }}
        >
          <div className="flex flex-col items-center">
            <div 
              className="w-16 h-16 border-4 border-gray-200 border-t-black rounded-full mb-4" 
              style={{ animation: 'spin 1s linear infinite' }}
            />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Procesando pago</h2>
            <p className="text-gray-600">Por favor espera un momento...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CardNetCheckoutScreen;